import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useDropzone } from "react-dropzone";
import { importarMidias, TipoMidiaN1 } from "../../services/materiaisN1";
import { TipoMaterialN1, TIPO_MATERIAL_LABELS } from "../../types/materiaisN1.types";

/**
 * FASE 05 — Ajustes 25/08 (plano de mídias, Etapa E — item 13 do docx de 22/08).
 *
 * Carga em massa de ícones, figuras ou fichas técnicas por .ZIP. O vínculo é pelo NOME DO
 * ARQUIVO: cada arquivo tem de casar com o campo "Nome do arquivo" de algum produto do tipo
 * selecionado. Empresa e referências vêm do acesso do usuário (F05A-P3c) — a tela não pergunta.
 *
 * Validação tudo-ou-nada: um arquivo sem vínculo cancela a importação inteira e a tela lista
 * os órfãos, para o usuário corrigir e reenviar.
 */
interface AbaImportarMidiasProps {
  tipoMaterial: TipoMaterialN1;
  onImportado: () => void;
}

const OPCOES_MIDIA: Array<{ valor: TipoMidiaN1; rotulo: string; extensoes: string }> = [
  { valor: 1, rotulo: "Ícones", extensoes: ".BMP" },
  { valor: 2, rotulo: "Figuras", extensoes: ".PNG, .JPG, .JPEG" },
  { valor: 3, rotulo: "Fichas técnicas", extensoes: ".PDF, .JPG, .JPEG" },
];

export const AbaImportarMidias: React.FC<AbaImportarMidiasProps> = ({
  tipoMaterial,
  onImportado,
}) => {
  const [tipoMidia, setTipoMidia] = useState<TipoMidiaN1>(2);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [orfaos, setOrfaos] = useState<string[]>([]);
  const [sucesso, setSucesso] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/zip": [".zip"], "application/x-zip-compressed": [".zip"] },
    multiple: false,
    disabled: enviando,
    onDrop: (aceitos) => {
      if (aceitos.length > 0) {
        setArquivo(aceitos[0]);
        setErro(null);
        setOrfaos([]);
        setSucesso(null);
      }
    },
  });

  const midiaAtual = OPCOES_MIDIA.find((o) => o.valor === tipoMidia)!;

  const handleImportar = async () => {
    if (!arquivo) return;

    setEnviando(true);
    setErro(null);
    setOrfaos([]);
    setSucesso(null);

    const r = await importarMidias(tipoMaterial, tipoMidia, arquivo);

    if (r.sucesso) {
      setSucesso(
        `${r.totalArquivos} arquivo(s) importado(s), alcançando ${r.totalProdutosAfetados} produto(s).`
      );
      setArquivo(null);
      onImportado();
    } else {
      setErro(r.mensagem ?? "Não foi possível importar as mídias.");
      setOrfaos(r.arquivosSemVinculo ?? []);
    }

    setEnviando(false);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: "0.85rem", color: "#666", mb: 1 }}>
        Tipo de mídia a importar para <strong>{TIPO_MATERIAL_LABELS[tipoMaterial]}</strong>:
      </Typography>

      <RadioGroup
        row
        value={tipoMidia}
        onChange={(e) => {
          setTipoMidia(Number(e.target.value) as TipoMidiaN1);
          setErro(null);
          setOrfaos([]);
          setSucesso(null);
        }}
        sx={{ mb: 2 }}
      >
        {OPCOES_MIDIA.map((o) => (
          <FormControlLabel
            key={o.valor}
            value={o.valor}
            control={<Radio size="small" sx={{ color: "#3C90AD", "&.Mui-checked": { color: "#3C90AD" } }} />}
            label={<Typography sx={{ fontSize: "0.8rem" }}>{o.rotulo}</Typography>}
            sx={{ mr: 3 }}
            disabled={enviando}
          />
        ))}
      </RadioGroup>

      <Box
        {...getRootProps()}
        sx={{
          p: 4,
          textAlign: "center",
          border: isDragActive ? "2px dashed #3C90AD" : "1px solid #e0e0e0",
          backgroundColor: isDragActive ? "#f0f8ff" : "#fff",
          cursor: enviando ? "not-allowed" : "pointer",
          minHeight: 180,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon sx={{ fontSize: 48, color: "#999", mb: 1 }} />
        <Typography variant="body1">
          {arquivo ? arquivo.name : "Arraste o arquivo .ZIP aqui ou clique para selecionar"}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          O .ZIP deve conter arquivos {midiaAtual.extensoes} na raiz (máx. 200 MB).
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 2, fontSize: "0.8rem" }}>
        O vínculo é pelo <strong>nome do arquivo</strong>: cada arquivo do .ZIP precisa ter o
        mesmo nome informado no campo "Nome do arquivo" de algum produto. Se um único arquivo
        não tiver vínculo, <strong>nada é importado</strong>.
      </Alert>

      {sucesso && (
        <Alert severity="success" sx={{ mt: 2, fontSize: "0.8rem" }}>
          {sucesso}
        </Alert>
      )}

      {erro && (
        <Alert severity="error" sx={{ mt: 2, fontSize: "0.8rem" }}>
          {erro}
          {orfaos.length > 0 && (
            <Box sx={{ mt: 1, maxHeight: 160, overflowY: "auto" }}>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 600, mb: 0.5 }}>
                Arquivos sem vínculo ({orfaos.length}):
              </Typography>
              {orfaos.map((o) => (
                <Typography key={o} sx={{ fontSize: "0.75rem" }}>
                  • {o}
                </Typography>
              ))}
            </Box>
          )}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleImportar}
          disabled={!arquivo || enviando}
          startIcon={enviando ? <CircularProgress size={18} color="inherit" /> : undefined}
          sx={{ backgroundColor: "#3C90AD", "&:hover": { backgroundColor: "#2d6f87" } }}
        >
          {enviando ? "IMPORTANDO..." : "IMPORTAR MÍDIAS"}
        </Button>
      </Box>
    </Box>
  );
};
